import { useState } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import {
  chargeTpp,
  checkinTpp,
  checkoutTpp,
  createTppCard,
  exportTppEvents,
  getTppZipConnection,
  importTppCardsExcel,
  patchTppCard,
  patchTppEvent,
  patchTppPoolSettings,
  tppCardCreatedToSummary,
  uploadTppEventProof,
  type TppCardSummary,
  type TppEventSummary,
} from '../../../services/transitPassPool.service';
import {
  type TppBorrowSubmitPayload,
  type TppChargeSubmitPayload,
  type TppReturnSubmitPayload,
} from './tppOpsModalTypes';
import type { TppEventEditSeed } from './TppEventEditModal';
import { tppEventSummaryToEditSeed } from './tppEventEditSeed';
import { normalizeFullWidthNumerics } from '../../../utils/numericInputNormalize';
import { tppMutationError } from '../../../utils/tppApiError';
import { deliverBlobToUser } from '../../../utils/downloadBlob';
import { isTppBorrowable } from './tppCardPredicates';

export type TppManageTab = 'list' | 'setup';

/** `['tpp-']` は `tpp-cards` 等にマッチしない（TanStack Query は要素完全一致の prefix） */
export function invalidateAllTppQueries(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({
    predicate: (q) => typeof q.queryKey[0] === 'string' && String(q.queryKey[0]).startsWith('tpp'),
  });
}

export type UseTppPageMutationsArgs = {
  poolKey: string;
  selected: TppCardSummary | null;
  setSelected: Dispatch<SetStateAction<TppCardSummary | null>>;
  setActiveTab: Dispatch<SetStateAction<TppManageTab>>;
  setFlashMessage: (message: string | null) => void;
  setFlashError: (message: string | null) => void;
};

/**
 * モーダル開閉状態と mutation を一括管理。
 * ページは open* / hide* / mut だけ渡せばよい。
 */
export function useTppPageMutations({
  poolKey,
  selected,
  setSelected,
  setActiveTab,
  setFlashMessage,
  setFlashError,
}: UseTppPageMutationsArgs) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [createCardOpen, setCreateCardOpen] = useState(false);
  const [editCardOpen, setEditCardOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [poolSettingsModalOpen, setPoolSettingsModalOpen] = useState(false);
  const [importExcelModalOpen, setImportExcelModalOpen] = useState(false);
  const [eventEditOpen, setEventEditOpen] = useState(false);
  const [eventEditSeed, setEventEditSeed] = useState<TppEventEditSeed | null>(null);
  /** 返却モーダル用。open 時点のカード経路（selected 同期待ちで空にしない） */
  const [returnRouteSeed, setReturnRouteSeed] = useState('');

  /** ハンドルありは Zip 書き戻し。手動DL は記録のみ（未DL バッジと CTA で案内） */
  const flashSaved = (action: string) => {
    if (getTppZipConnection()?.autoSaveMode === 'file-handle') {
      setFlashMessage(
        t('tpp.flash_saved_file', { action })
      );
      return;
    }
    setFlashMessage(action);
  };
  const flashMutError = (error: unknown) => {
    setFlashError(tppMutationError(error, t));
  };
  const refreshTpp = () => invalidateAllTppQueries(qc);

  const checkoutMut = useMutation({
    mutationFn: (payload: TppBorrowSubmitPayload) =>
      checkoutTpp(selected!.id, {
        poolKey,
        outDate: payload.outDate,
        purpose: payload.purpose,
        place: payload.place,
        route: payload.route,
      }),
    onMutate: () => {
      setCheckoutOpen(false);
    },
    onError: (error) => {
      setCheckoutOpen(true);
      flashMutError(error);
    },
    onSuccess: () => {
      flashSaved(t('tpp.flash_borrow_action'));
      void refreshTpp();
    },
  });

  const checkinMut = useMutation({
    mutationFn: (payload: TppReturnSubmitPayload) => {
      if (!selected?.openEventId) {
        throw new Error('missing');
      }
      return checkinTpp(selected.openEventId, {
        inDate: payload.inDate,
        amount: normalizeFullWidthNumerics(payload.amount).trim(),
        route: payload.route,
        proof: payload.proof,
      });
    },
    onMutate: () => {
      setCheckinOpen(false);
    },
    onError: (error) => {
      setCheckinOpen(true);
      flashMutError(error);
    },
    onSuccess: () => {
      flashSaved(t('tpp.flash_return_action'));
      void refreshTpp();
    },
  });

  const chargeMut = useMutation({
    mutationFn: (payload: TppChargeSubmitPayload) =>
      chargeTpp(selected!.id, {
        poolKey,
        outDate: payload.outDate,
        amount: normalizeFullWidthNumerics(payload.amount).trim(),
        note: payload.note || undefined,
      }),
    onMutate: () => {
      setChargeOpen(false);
    },
    onError: (error) => {
      setChargeOpen(true);
      flashMutError(error);
    },
    onSuccess: () => {
      flashSaved(t('tpp.flash_charge_action'));
      void refreshTpp();
    },
  });

  const createCardMut = useMutation({
    mutationFn: (payload: { displayNumber: string; physicalId: string }) =>
      createTppCard({
        poolKey,
        displayNumber: Number(normalizeFullWidthNumerics(payload.displayNumber).trim()),
        physicalId: payload.physicalId.trim(),
      }),
    onSuccess: async (created) => {
      setCreateCardOpen(false);
      setSelected(tppCardCreatedToSummary(created));
      setActiveTab('list');
      flashSaved(
        t('tpp.flash_create_card_action', { n: created.displayNumber })
      );
      await refreshTpp();
    },
  });

  const patchCardMut = useMutation({
    mutationFn: (payload: { physicalId: string; isActive: boolean }) =>
      patchTppCard(selected!.id, {
        physicalId: payload.physicalId.trim(),
        isActive: payload.isActive,
      }),
    onSuccess: async () => {
      setEditCardOpen(false);
      flashSaved(t('tpp.flash_edit_card_action'));
      await refreshTpp();
    },
  });

  const uploadProofMut = useMutation({
    mutationFn: ({ eventId, file }: { eventId: number; file: File }) =>
      uploadTppEventProof(eventId, file),
    onSuccess: async () => {
      flashSaved(t('tpp.flash_proof_upload_action'));
      await refreshTpp();
    },
  });

  const patchEventMut = useMutation({
    mutationFn: (payload: {
      id: number;
      eventKind: 'usage' | 'charge';
      isOpenCheckout: boolean;
      purpose: string;
      place: string;
      route: string;
      amountStr: string;
      outDate: string;
      inDate: string;
      departmentLabel: string;
      borrowerName: string;
    }) => {
      const isCharge = payload.eventKind === 'charge';
      const body: Record<string, unknown> = {
        purpose: payload.purpose,
        place: isCharge ? '' : payload.place,
        route: isCharge ? '' : payload.route,
        departmentLabel: isCharge ? '' : payload.departmentLabel,
        borrowerName: isCharge ? '' : payload.borrowerName,
      };
      if (payload.outDate) {
        body.outDate = payload.outDate;
      }
      if (isCharge) {
        body.inDate = payload.outDate || null;
      } else if (!payload.isOpenCheckout) {
        // 貸出中は返却日を送らない（返却モーダル以外で返却扱いにしない）
        body.inDate = payload.inDate === '' ? null : payload.inDate || null;
      }
      const amountTrimmed = normalizeFullWidthNumerics(payload.amountStr).trim();
      body.amount = amountTrimmed === '' ? null : amountTrimmed;
      return patchTppEvent(payload.id, body);
    },
    onSuccess: async () => {
      setEventEditOpen(false);
      setEventEditSeed(null);
      flashSaved(t('tpp.flash_edit_event_action'));
      await refreshTpp();
    },
  });

  const exportMut = useMutation({
    mutationFn: async (payload: { dateFrom: string; dateTo: string }) => {
      const body: Record<string, unknown> = { poolKey };
      if (payload.dateFrom) body.dateFrom = payload.dateFrom;
      if (payload.dateTo) body.dateTo = payload.dateTo;
      return exportTppEvents(body);
    },
    onSuccess: ({ blob, filename }) => {
      deliverBlobToUser(blob, filename);
      setExportModalOpen(false);
      setFlashMessage(
        t('tpp.flash_export_action')
      );
    },
  });

  const poolSettingsMut = useMutation({
    mutationFn: (payload: {
      displayPrefix: string;
      displaySuffix: string;
      isoCurrencyCode: string | null;
      fractionDigits: number;
      inputRoundingMode: string;
      displayRoundingMode: string;
    }) => patchTppPoolSettings(poolKey, payload),
    onSuccess: async () => {
      setPoolSettingsModalOpen(false);
      flashSaved(t('tpp.flash_pool_settings_action'));
      await refreshTpp();
    },
  });

  const importExcelMut = useMutation({
    mutationFn: (file: File) => importTppCardsExcel(file),
    onSuccess: async (result) => {
      setImportExcelModalOpen(false);
      setActiveTab('list');
      const skipped = result.errors?.length ?? 0;
      flashSaved(
        skipped > 0
          ? t('tpp.flash_import_partial', { created: result.created,
              updated: result.updated,
              skipped })
          : t('tpp.flash_import_action', { created: result.created,
              updated: result.updated })
      );
      await refreshTpp();
    },
  });

  const openCreateCardModal = () => {
    createCardMut.reset();
    setCreateCardOpen(true);
  };

  const openEditCardModal = (card: TppCardSummary) => {
    patchCardMut.reset();
    setSelected(card);
    setEditCardOpen(true);
  };

  const openExportModal = () => {
    exportMut.reset();
    setExportModalOpen(true);
  };

  const openPoolSettingsModal = () => {
    poolSettingsMut.reset();
    setPoolSettingsModalOpen(true);
  };

  const openImportExcelModal = () => {
    importExcelMut.reset();
    setImportExcelModalOpen(true);
  };

  const openBorrowFor = (c: TppCardSummary) => {
    if (!isTppBorrowable(c)) {
      setSelected(c);
      return;
    }
    checkoutMut.reset();
    setSelected(c);
    setCheckoutOpen(true);
  };

  const openReturnFor = (c: TppCardSummary) => {
    checkinMut.reset();
    setReturnRouteSeed(c.openEventRoute?.trim() ?? '');
    setSelected(c);
    setCheckinOpen(true);
  };

  const openChargeFor = (c: TppCardSummary) => {
    chargeMut.reset();
    setSelected(c);
    setChargeOpen(true);
  };

  const openEditEvent = (ev: TppEventSummary) => {
    patchEventMut.reset();
    setEventEditSeed(tppEventSummaryToEditSeed(ev));
    setEventEditOpen(true);
  };

  return {
    refreshTpp,
    checkoutMut,
    checkinMut,
    chargeMut,
    createCardMut,
    patchCardMut,
    uploadProofMut,
    patchEventMut,
    exportMut,
    poolSettingsMut,
    importExcelMut,
    openBorrowFor,
    openReturnFor,
    openChargeFor,
    openCreateCardModal,
    openEditCardModal,
    openExportModal,
    openPoolSettingsModal,
    openImportExcelModal,
    openEditEvent,
    modals: {
      checkoutOpen,
      checkinOpen,
      chargeOpen,
      createCardOpen,
      editCardOpen,
      exportModalOpen,
      poolSettingsModalOpen,
      importExcelModalOpen,
      eventEditOpen,
      eventEditSeed,
      returnRouteSeed,
      hideCheckout: () => setCheckoutOpen(false),
      hideCheckin: () => setCheckinOpen(false),
      hideCharge: () => setChargeOpen(false),
      hideCreateCard: () => setCreateCardOpen(false),
      hideEditCard: () => setEditCardOpen(false),
      hideExport: () => setExportModalOpen(false),
      hidePoolSettings: () => setPoolSettingsModalOpen(false),
      hideImportExcel: () => setImportExcelModalOpen(false),
      hideEventEdit: () => {
        setEventEditOpen(false);
        setEventEditSeed(null);
      },
    },
  };
}
